import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { uploadDocument } from '../../../services/documentService';

/**
 * Document Upload Component
 * Handles file upload with drag-and-drop, validation, and progress tracking
 */
const DocumentUpload = ({ onClose, onSuccess, userId }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const allowedTypes = {
    'application/pdf': 'PDF',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'text/plain': 'TXT'
  };

  const handleDragEnter = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setIsDragging(false);

    const files = e?.dataTransfer?.files;
    if (files && files?.length > 0) {
      handleFileSelection(files?.[0]);
    }
  };

  const handleFileInputChange = (e) => {
    const files = e?.target?.files;
    if (files && files?.length > 0) {
      handleFileSelection(files?.[0]);
    }
  };

  const handleFileSelection = (file) => {
    setError(null);

    // Validate file type
    if (!allowedTypes?.[file?.type]) {
      setError('Invalid file type. Only PDF, DOCX, and TXT files are allowed.');
      return;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file?.size > maxSize) {
      setError('File size exceeds 10MB limit.');
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Simulate progress (real progress would require chunked upload)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      await uploadDocument(selectedFile, userId);

      clearInterval(progressInterval);
      setUploadProgress(100);

      setTimeout(() => {
        onSuccess?.();
      }, 500);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err?.message || 'Failed to upload document. Please try again.');
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-2xl w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Upload" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Upload Document</h2>
              <p className="text-sm text-muted-foreground">Add documents to the chatbot knowledge base</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isUploading}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* File Drop Zone */}
          <div
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !selectedFile && !isUploading && fileInputRef?.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
              isDragging
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary hover:bg-primary/5'
            } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt"
              onChange={handleFileInputChange}
              className="hidden"
              disabled={isUploading}
            />

            {!selectedFile ? (
              <>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="FileText" size={32} className="text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Drop your document here
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  or click to browse your files
                </p>
                <div className="flex items-center justify-center space-x-4 text-xs text-muted-foreground">
                  <span className="flex items-center space-x-1">
                    <Icon name="CheckCircle" size={14} />
                    <span>PDF, DOCX, TXT</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Icon name="CheckCircle" size={14} />
                    <span>Max 10MB</span>
                  </span>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded flex items-center justify-center">
                    <Icon name="FileText" size={20} className="text-primary" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-foreground">{selectedFile?.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(selectedFile?.size)} • {allowedTypes?.[selectedFile?.type]}
                    </p>
                  </div>
                </div>
                {!isUploading && (
                  <button
                    onClick={(e) => {
                      e?.stopPropagation();
                      setSelectedFile(null);
                      setError(null);
                    }}
                    className="text-error hover:text-error/80 transition-colors"
                  >
                    <Icon name="X" size={20} />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Uploading...</span>
                <span className="text-sm font-medium text-primary">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className="bg-primary h-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-4 bg-error/10 border border-error/20 rounded-lg p-3 flex items-center space-x-2">
              <Icon name="AlertTriangle" size={16} className="text-error" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {/* Info Box */}
          <div className="mt-4 bg-primary/5 border border-primary/20 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-primary mb-2 flex items-center space-x-2">
              <Icon name="Info" size={16} />
              <span>Processing Information</span>
            </h4>
            <ul className="text-xs text-muted-foreground space-y-1 ml-6 list-disc">
              <li>Documents will be automatically processed after upload</li>
              <li>Text will be extracted and split into searchable chunks</li>
              <li>AI embeddings will be generated for semantic search</li>
              <li>Processing may take 1-5 minutes depending on document size</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            className="flex items-center space-x-2"
          >
            {isUploading ? (
              <>
                <Icon name="Loader" size={16} className="animate-spin" />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <Icon name="Upload" size={16} />
                <span>Upload Document</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;